import type { Rng } from './rng.js';

/**
 * Броски и выплаты. Пакет правил не знает про контент-паки: сюда приезжает
 * ровно та форма, которую отдаёт сборка контента, и всё считается на месте.
 */

export type StatName = 'sila' | 'smekalochka' | 'obayanie' | 'chotkost' | 'avtoritet';

export interface Doer {
  sila: number;
  smekalochka: number;
  obayanie: number;
  chotkost: number;
  avtoritet: number;
  stress: number;
}

export interface JobSpec {
  difficulty: number;
  stats: Partial<Record<StatName, number>>;
  payout?: number;
  loot?: { value: number };
  heat: number;
  stress: number;
}

export interface JobOutcome {
  success: boolean;
  chance: number;
  /** Деньги в копейках; при провале ноль. */
  payout: number;
  /** Стоимость добытого хабара в копейках. */
  lootValue: number;
  heat: number;
  stress: number;
}

/** Сколько хабара унесёшь в руках. С тачками в M4 это станет функцией. */
export const CARRY_ON_FOOT = 2;

const MIN_CHANCE = 0.05;
const MAX_CHANCE = 0.95;
const BASE_CHANCE = 0.35;
const PER_POINT = 0.035;
const STRESS_FREE = 60;
const STRESS_PENALTY = 0.006;
/** Провал не отменяет риска: палево и нервы всё равно набегают. */
const FAIL_PENALTY = 1.5;

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

/** Сумма статов с весами дела: у форточника вскрытие тянет смекалочка. */
export function effectiveStats(job: JobSpec, doer: Doer): number {
  let sum = 0;
  for (const [stat, weight] of Object.entries(job.stats)) {
    sum += (doer[stat as StatName] ?? 0) * (weight ?? 0);
  }
  return sum;
}

/** Штраф за нервы: выше шестидесяти стресса руки уже трясутся. */
export function stressPenalty(stress: number): number {
  return Math.max(0, stress - STRESS_FREE) * STRESS_PENALTY;
}

export function successChance(job: JobSpec, doer: Doer): number {
  const advantage = effectiveStats(job, doer) - job.difficulty;
  return clamp(BASE_CHANCE + PER_POINT * advantage - stressPenalty(doer.stress), MIN_CHANCE, MAX_CHANCE);
}

export function rollJob(job: JobSpec, doer: Doer, rng: Rng): JobOutcome {
  const chance = successChance(job, doer);
  const success = rng.chance(chance);
  // Разброс выплаты ±15%: одинаковые дела не должны давать одинаковые числа.
  const spread = 0.85 + rng.next() * 0.3;

  if (!success) {
    return {
      success: false,
      chance,
      payout: 0,
      lootValue: 0,
      heat: Math.round(job.heat * FAIL_PENALTY),
      stress: Math.round(job.stress * FAIL_PENALTY),
    };
  }

  return {
    success: true,
    chance,
    payout: job.payout ? Math.round(job.payout * spread) : 0,
    lootValue: job.loot ? Math.round(job.loot.value * spread) : 0,
    heat: job.heat,
    stress: job.stress,
  };
}

/** Сколько отдаст приёмка за весь хабар с учётом комиссии. */
export function sellValue(items: readonly { value: number }[], commission: number): number {
  const total = items.reduce((sum, item) => sum + item.value, 0);
  return Math.round(total * (1 - commission));
}

/** Доход точки за ночь. */
export function nightIncome(incomePerHour: number, level: number, nightHours: number): number {
  return Math.round(incomePerHour * (1 + 0.15 * (level - 1)) * nightHours);
}
